import { useRegister } from "@/hooks/data/useRegister"
import { registerSchema } from "@oblique/api/types"
import { useForm, useStore } from "@tanstack/react-form"
import { useEffect, useState } from "react"
import {
	Link,
	Navigate,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router"
import { Button } from "../components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../components/ui/card"
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../components/ui/form"
import { Input } from "../components/ui/input"
import { useAuth } from "../providers/auth"

export const Register = () => {
	const [searchParams] = useSearchParams()
	const location = useLocation()
	const locationState = location.state as {
		from?: { pathname?: string }
	} | null
	const navigate = useNavigate()
	const { status, getGoogleSignInUrl, user } = useAuth()

	const redirect =
		searchParams.get("redirect") ??
		locationState?.from?.pathname ??
		"/dashboard"

	useEffect(() => {
		if (status === "authenticated" && user) {
			navigate(redirect, { replace: true })
		}
	}, [status, user, navigate, redirect])

	const [submitError, setSubmitError] = useState<string | null>(null)
	const registerMutation = useRegister(redirect)

	const form = useForm({
		defaultValues: {
			name: "",
			email: "",
			password: "",
		},
		validators: {
			onSubmit: registerSchema,
		},
		onSubmit: async ({ value }) => {
			setSubmitError(null)

			try {
				await registerMutation.mutateAsync(value)
			} catch (error) {
				if (error instanceof Error && error.message) {
					setSubmitError(error.message)
				} else {
					setSubmitError("Unable to create your account. Please try again.")
				}
			}
		},
	})

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
	const isBusy = isSubmitting || registerMutation.isPending

	if (status === "authenticated" && user) {
		return <Navigate to={redirect} replace />
	}

	return (
		<div className="mx-auto flex w-full max-w-sm flex-col gap-6 p-6">
			<Card>
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-semibold">
						Create account
					</CardTitle>
					<CardDescription>Join Oblique.</CardDescription>
				</CardHeader>
				<CardContent>
					<Form form={form}>
						<form
							className="space-y-6"
							onSubmit={(event) => {
								event.preventDefault()
								event.stopPropagation()
								void form.handleSubmit()
							}}
							noValidate
						>
							<FormField
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Name</FormLabel>
										<FormControl>
											<Input
												name={field.name}
												value={(field.value as string) ?? ""}
												onBlur={field.onBlur}
												onChange={(event) => {
													setSubmitError(null)
													field.onChange(event)
												}}
												placeholder="Ada Lovelace"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												name={field.name}
												value={(field.value as string) ?? ""}
												onBlur={field.onBlur}
												onChange={(event) => {
													setSubmitError(null)
													field.onChange(event)
												}}
												type="email"
												autoComplete="email"
												placeholder="you@example.com"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Password</FormLabel>
										<FormControl>
											<Input
												name={field.name}
												value={(field.value as string) ?? ""}
												onBlur={field.onBlur}
												onChange={(event) => {
													setSubmitError(null)
													field.onChange(event)
												}}
												type="password"
												autoComplete="new-password"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{submitError ? (
								<p className="text-sm font-medium text-destructive">
									{submitError}
								</p>
							) : null}

							<Button type="submit" className="w-full" disabled={isBusy}>
								{isBusy ? "Creating account…" : "Create account"}
							</Button>
						</form>
					</Form>
				</CardContent>
				<CardFooter className="flex flex-col gap-4">
					<Button
						type="button"
						variant="outline"
						className="w-full"
						disabled={isBusy}
						onClick={() => {
							const url = getGoogleSignInUrl(
								redirect !== "/dashboard" ? redirect : undefined,
							)
							window.location.href = url
						}}
					>
						Continue with Google
					</Button>
					<p className="text-center text-sm text-muted-foreground">
						Already have an account?{" "}
						<Button variant="link" className="px-1" asChild>
							<Link to={`/login?redirect=${encodeURIComponent(redirect)}`}>
								Sign in
							</Link>
						</Button>
					</p>
				</CardFooter>
			</Card>
		</div>
	)
}
